<!-- BatchDeletePaymentForm.vue -->
<script setup>
import { ref, computed } from "vue";
import { usePaymentStore } from "@/stores/usePaymentStore";

const props = defineProps({
  paymentIds: {
    type: Array,
    required: true,
  },
});
import { useDraggableModal } from "@/composables/useDraggableModal";

const emit = defineEmits(["close", "refresh"]);
const store = usePaymentStore();

// 🔹 NOVO: arrastar o modal pelo cabeçalho (posição não persiste entre aberturas)
const { panelRef, panelStyle, isDragging, isDraggable, onHandlePointerDown } =
  useDraggableModal();

const isLoading    = ref(false);
const errorMessage = ref("");

const count = computed(() => props.paymentIds.length);

async function handleConfirm() {
  errorMessage.value = "";
  isLoading.value    = true;
  try {
    await store.deleteSelectedPayments();
    emit("refresh");
  } catch (error) {
    errorMessage.value =
      error.response?.data?.message ||
      error.message ||
      "Erro ao excluir payments.";
  } finally {
    isLoading.value = false;
  }
}
</script>

<template>
  <div class="modal-backdrop" @click.self="emit('close')">
    <div ref="panelRef" class="modal-panel" :style="panelStyle">
      <!-- Cabeçalho -->
      <div
        class="modal-header"
        :class="{
 'modal-header--draggable': isDraggable,
          'modal-header--dragging': isDragging,
        }"
        @pointerdown="onHandlePointerDown"
      >
        <h2 class="modal-title">Exclusão em lote</h2>
        <button class="modal-close-btn" :disabled="isDeleting" @click="emit('close')">
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0
                 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10
                 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293
                 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"/>
          </svg>
        </button>
      </div>

      <!-- Corpo -->
      <div class="modal-body">
        <div v-if="errorMessage" class="form-error">{{ errorMessage }}</div>

        <div class="confirm-icon">
          <i class="fas fa-trash-alt"></i>
        </div>

        <p class="confirm-text">
          Você está prestes a excluir
          <strong>{{ count }} payment{{ count !== 1 ? "s" : "" }}</strong>
          permanentemente.
          <br />
          <span class="confirm-sub">Esta ação não pode ser desfeita.</span>
        </p>

        <!-- Lista de IDs selecionados (até 10; restante é truncado) -->
        <div class="ids-box">
          <span
            v-for="id in paymentIds.slice(0, 10)"
            :key="id"
            class="id-chip"
          >#{{ id }}</span>
          <span v-if="count > 10" class="id-chip id-chip-more">
            +{{ count - 10 }} mais
          </span>
        </div>
      </div>

      <!-- Rodapé -->
      <div class="modal-footer">
        <button class="btn btn-secondary" @click="emit('close')" :disabled="isLoading">
          Cancelar
        </button>
        <button class="btn btn-danger" @click="handleConfirm" :disabled="isLoading">
            <svg v-if="isLoading" class="w-5 h-5 mr-2 animate-spin" viewBox="0 0 24 24" fill="none">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/>
            </svg>
            <svg v-else class="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd"
                d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                clip-rule="evenodd"/>
            </svg>
            <span>{{ isLoading ? "Excluindo…" : "Confirmar exclusão" }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.modal-panel {
  max-height: calc(100vh - 40px);
  max-width: 440px;
}

.modal-body {
  align-items: center;
}

.form-error {
  width: 100%;
  background: var(--danger-bg);
  border: 1px solid var(--danger);
  color: var(--danger-text);
  border-radius: 8px;
  padding: 0.6rem 0.9rem;
  font-size: 0.85rem;
}

.confirm-icon {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: var(--danger-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  color: var(--danger-hover);
}

.confirm-text {
  text-align: center;
  font-size: 0.9rem;
  color: var(--text-dark);
  line-height: 1.5;
}

.confirm-sub {
  font-size: 0.8rem;
  color: var(--text-light);
}

.ids-box {
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  justify-content: center;
}

.id-chip {
  display: inline-block;
  padding: 0.2rem 0.55rem;
  border-radius: 9999px;
  background: #f1f3f5;
  color: #495057;
  font-size: 0.75rem;
  font-weight: 600;
  border: 1px solid var(--border-color);
}

.id-chip-more {
  background: #ffe8cc;
  color: #9c4a00;
  border-color: #ffa94d;
}

</style>